import React, { useId } from "react";

const optionStyle = (selected, optionDisabled) => ({
  minHeight: 42,
  border: `1px solid var(--${selected ? "Brand1-PenBlue" : "Grey25"})`,
  cursor: optionDisabled ? "not-allowed" : "pointer",
  opacity: optionDisabled ? 0.55 : 1,
});

const normaliseOptions = (options = []) =>
  options.map((option) => {
    if (typeof option !== "object") {
      return { value: option, label: option, disabled: false };
    }

    return {
      ...option,
      value: option.value ?? option.id,
      label: option.label ?? option.name,
    };
  });

/**
 * A persistent list of selectable options.
 *
 * When `multiple` is false, `value` and `onChange` use a single value.
 * When `multiple` is true, they use an array of values.
 */
const OptionList = ({
  label,
  name,
  options = [],
  value,
  onChange,
  multiple = false,
  required = false,
  disabled = false,
  className = "",
}) => {
  const generatedName = useId();
  const inputName = name || generatedName;
  const parsedOptions = normaliseOptions(options);
  const selectedValues = multiple && Array.isArray(value) ? value : [];

  const isSelected = (optionValue) =>
    multiple
      ? selectedValues.includes(optionValue)
      : value === optionValue;

  const handleChange = (optionValue) => {
    if (multiple) {
      const nextValue = selectedValues.includes(optionValue)
        ? selectedValues.filter((item) => item !== optionValue)
        : [...selectedValues, optionValue];

      onChange?.(nextValue);
      return;
    }

    onChange?.(optionValue);
  };

  return (
    <div
      className={`fit-column gap-s${className ? ` ${className}` : ""}`}
      role={multiple ? "group" : "radiogroup"}
      aria-label={!label ? name : undefined}
      aria-disabled={disabled}
    >
      {label && (
        <div className="FP_Subtitle_Semibold text-primary-1">
          {label}
          {required && (
            <span className="text-error" aria-hidden="true">
              *
            </span>
          )}
        </div>
      )}

      <div className="fit-column gap-s">
        {parsedOptions.map((option) => {
          const selected = isSelected(option.value);
          const optionDisabled = disabled || option.disabled;

          return (
            <label
              key={option.value}
              className={`selectable-option row-space-between gap-s p-s bg-white all-radius-s width-full FP_Body ${selected ? 'FP_Body_Semibold text-primary-1' : 'FP_Body text_black'} ${optionDisabled ? 'text-grey-50' : ''}`}
              style={optionStyle(selected, optionDisabled)}
            >
              <span className="flex-1 text-align-left">
                {option.label}
              </span>

              <input
                className="penblue-control"
                type={multiple ? "checkbox" : "radio"}
                name={multiple ? `${inputName}[]` : inputName}
                value={option.value}
                checked={selected}
                disabled={optionDisabled}
                required={required && !multiple}
                onChange={() => handleChange(option.value)}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default OptionList;
